// UnprocessedDownloadsTray
//
// Shows the user their "Ambient Captures" — spiritual downloads that arrived
// via Siri Shortcut, IFTTT, Alexa Routine, or PWA background worker while
// they were away from the app.
//
// Behavior:
// - Polls /api/ambient/captures every 45s so new arrivals appear naturally.
// - Displays a pulsating Burnished Gold mic while polling / processing.
// - Each capture shows source, device label, time, category, themes, and a
//   preview of the plaintext (server-side pending review).
// - "Save to Journey" promotes the capture: encrypts the reviewed text
//   client-side (using existing PBKDF2 key) and creates a proper Download
//   entry, then removes the plaintext from the ambient tray.
// - "Discard" removes the pending capture without saving.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Mic, MicOff, Check, Trash2, RefreshCw, Loader2, Zap, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { api } from '../utils/api';
import { encryption, sessionKeyManager } from '../utils/encryption';
import { getCategoryBadgeStyle, getCategoryHex } from '../utils/categoryColors';
import { useSelahRest } from '../contexts/SelahRestContext';

const POLL_INTERVAL_MS = 45_000;

const SourceBadge = ({ source, deviceLabel }) => {
  const label = deviceLabel
    ? `${source} • ${deviceLabel}`
    : source || 'webhook';
  return (
    <span className="text-[11px] uppercase tracking-wider text-[var(--sd-muted-fg)] font-medium">
      {label}
    </span>
  );
};

const PulsingGoldMic = ({ active = true, size = 20 }) => (
  <div className="relative inline-flex items-center justify-center">
    <div
      className="absolute inset-0 rounded-full"
      style={{
        boxShadow: active ? '0 0 0 0 rgba(212, 175, 55, 0.55)' : 'none',
        animation: active ? 'pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none',
      }}
    />
    <Mic
      size={size}
      className="text-[var(--sd-primary)]"
      style={{ filter: active ? 'drop-shadow(0 0 6px rgba(212, 175, 55, 0.35))' : 'none' }}
    />
  </div>
);

const UnprocessedDownloadsTray = ({ onPromoted, defaultOpen = true }) => {
  const { startRest } = useSelahRest();
  const [captures, setCaptures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [expanded, setExpanded] = useState(defaultOpen);
  const [error, setError] = useState(null);

  const fetchCaptures = useCallback(async () => {
    try {
      const data = await api.listAmbientCaptures('pending');
      setCaptures(Array.isArray(data) ? data : []);
      setError(null);
      setLastFetched(new Date());
    } catch (e) {
      // Non-fatal — log and surface as inline hint
      console.error('ambient captures fetch failed', e);
      setError(e?.message || 'Could not load ambient captures.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCaptures();
    const iv = setInterval(fetchCaptures, POLL_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [fetchCaptures]);

  const handlePromote = async (capture) => {
    if (!capture?.id) return;
    setBusyId(capture.id);
    try {
      const email = sessionKeyManager.getUserEmail();
      if (!email) {
        toast.error('Encryption key not available. Please log out and sign in again.');
        return;
      }
      const key = await sessionKeyManager.getKey();
      const { ciphertext, iv } = await encryption.encrypt(capture.text, key);

      await api.promoteAmbientCapture(capture.id, ciphertext, iv);
      toast.success('Saved to your journey', {
        description: 'This capture has been encrypted and moved into your downloads.',
      });
      setCaptures((prev) => prev.filter((c) => c.id !== capture.id));
      // Post-ingestion ritual: enter the 60-second Selah Rest window.
      startRest({
        onComplete: () => {
          if (typeof onPromoted === 'function') onPromoted(capture);
        },
      });
    } catch (e) {
      console.error('promote error', e);
      toast.error('Could not save this capture', {
        description: e?.message || 'Please try again in a moment.',
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDiscard = async (capture) => {
    if (!capture?.id) return;
    if (!window.confirm('Discard this ambient capture? It will be removed permanently.')) return;
    setBusyId(capture.id);
    try {
      await api.discardAmbientCapture(capture.id);
      toast.success('Capture discarded');
      setCaptures((prev) => prev.filter((c) => c.id !== capture.id));
    } catch (e) {
      console.error('discard error', e);
      toast.error('Could not discard capture', { description: e?.message });
    } finally {
      setBusyId(null);
    }
  };

  const hasCaptures = captures.length > 0;
  const showEmpty = !loading && !hasCaptures && !error;

  const headerNote = useMemo(() => {
    if (loading) return 'Checking for ambient captures…';
    if (error) return 'Couldn’t reach the ambient inbox.';
    if (!hasCaptures) return 'Nothing waiting right now. Anything captured hands-free will appear here.';
    const n = captures.length;
    return `${n} capture${n === 1 ? '' : 's'} waiting for your review.`;
  }, [loading, error, hasCaptures, captures.length]);

  return (
    <Card
      className="glass-card p-5 border-[var(--sd-primary)]/25"
      data-testid="unprocessed-downloads-tray"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <PulsingGoldMic active={loading || hasCaptures} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[var(--sd-fg)] flex items-center gap-2">
              Unprocessed Downloads
              {hasCaptures && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold bg-[var(--sd-primary)] text-[var(--sd-primary-fg)]">
                  {captures.length}
                </span>
              )}
            </h3>
            <p className="text-xs text-[var(--sd-muted-fg)] mt-0.5">{headerNote}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchCaptures}
            className="text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)]"
            data-testid="unprocessed-refresh-button"
            title="Refresh"
            disabled={loading}
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <RefreshCw size={16} />
            )}
          </Button>
          {hasCaptures && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)]"
              data-testid="unprocessed-toggle-expand"
            >
              {expanded ? 'Collapse' : 'Show'}
            </Button>
          )}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="flex items-start gap-2 text-xs text-[var(--sd-muted-fg)] p-3 rounded-lg border border-[var(--sd-border)] bg-[var(--sd-surface)]">
          <AlertCircle size={14} className="mt-0.5 text-[#C88484]" />
          <span>{error}</span>
        </div>
      )}

      {/* Empty state — gentle onboarding hint */}
      {showEmpty && (
        <div className="border border-dashed border-[var(--sd-border)] rounded-xl p-5 text-center">
          <MicOff size={20} className="mx-auto mb-2 text-[var(--sd-muted-fg)]" />
          <p className="text-sm text-[var(--sd-fg)] font-medium">Your ambient inbox is quiet.</p>
          <p className="text-xs text-[var(--sd-muted-fg)] mt-1">
            Downloads sent hands-free from Siri, IFTTT, or Alexa will land here for your review.
            You can enable those any time from Settings — no rush.
          </p>
        </div>
      )}

      {/* Captures list */}
      <AnimatePresence initial={false}>
        {expanded && hasCaptures && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-3"
          >
            {captures.map((c) => {
              const busy = busyId === c.id;
              const catHex = c.analysis?.category ? getCategoryHex(c.analysis.category) : null;
              const cardAccentStyle = catHex
                ? { '--sd-primary': catHex, '--sd-primary-fg': '#1C1E21' }
                : undefined;
              return (
                <div
                  key={c.id}
                  className="p-4 rounded-xl border border-[var(--sd-border)] bg-[var(--sd-surface)]"
                  data-testid={`ambient-capture-${c.id}`}
                  style={{
                    ...(cardAccentStyle || {}),
                    ...(catHex ? { borderLeft: `3px solid ${catHex}` } : {}),
                  }}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Zap size={13} className="text-[var(--sd-primary)]" />
                      <SourceBadge source={c.source} deviceLabel={c.device_label} />
                      {c.analysis?.category && (
                        <Badge
                          style={getCategoryBadgeStyle(c.analysis.category)}
                          className="border text-[11px]"
                        >
                          {c.analysis.category}
                        </Badge>
                      )}
                    </div>
                    <span className="text-[11px] text-[var(--sd-muted-fg)] whitespace-nowrap">
                      {c.created_at ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true }) : ''}
                    </span>
                  </div>

                  <p className="text-sm text-[var(--sd-fg)] leading-relaxed mb-3 whitespace-pre-line">
                    {c.text}
                  </p>

                  {c.analysis?.themes?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {c.analysis.themes.slice(0, 4).map((t, i) => (
                        <span
                          key={i}
                          className="text-[11px] px-2 py-0.5 rounded-md bg-[var(--sd-surface-hover)] text-[var(--sd-fg)] border border-[var(--sd-border)]"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDiscard(c)}
                      disabled={busy}
                      className="text-xs text-[var(--sd-muted-fg)] hover:text-[#C88484]"
                      data-testid={`discard-capture-${c.id}`}
                    >
                      <Trash2 size={13} className="mr-1" />
                      Discard
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handlePromote(c)}
                      disabled={busy}
                      className="bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)] text-xs"
                      data-testid={`promote-capture-${c.id}`}
                    >
                      {busy ? (
                        <Loader2 size={13} className="mr-1 animate-spin" />
                      ) : (
                        <Check size={13} className="mr-1" />
                      )}
                      Save to Journey
                    </Button>
                  </div>
                </div>
              );
            })}

            {lastFetched && (
              <p className="text-[11px] text-[var(--sd-muted-fg)] text-right pt-1">
                Last checked {format(lastFetched, 'h:mm a')}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};

export default UnprocessedDownloadsTray;
